/**
 * Task-specific date utilities.
 *
 * Scope: Date operations tied to Task domain logic - checking if tasks
 * are overdue, due today, formatting task due dates, urgency levels,
 * time-based grouping, and sorting.
 *
 * IMPORTANT: Task date fields are displayed and compared in America/New_York
 * timezone to match backend `TASK_DATE_TZ` (app/defs/tasks.py). This ensures
 * the same task never shows different calendar days across machines.
 *
 * DO NOT use `new Date(task.due_date).toLocaleDateString(...)` or similar
 * browser-local Date formatting. Always use the helpers in this file.
 *
 * For generic ISO date primitives (format, parse, validate), use dateUtils.ts
 */

import { formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

import { Task } from '@/services/types/client-intake-types';
import { CanonicalTaskStatus } from '@/services/types/task-types';
import { TASK_TIMEZONE } from '@/utils/dateUtils';

// Re-export for convenience
export { TASK_TIMEZONE };

// ====== DATE PARSING & VALIDATION ======

/**
 * Safely parse a date string into a Date object.
 * Note: This returns the raw UTC Date. For NY-normalized comparisons,
 * use getTaskDateInNY() or the overdue/due today helpers.
 */
export const parseTaskDate = (dateString?: string | null): Date | null => {
  if (!dateString) return null;

  try {
    const parsed = parseISO(dateString);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Get the ISO date (YYYY-MM-DD) of a task datetime in NY timezone.
 * This is the canonical way to extract a date from task datetime fields.
 */
export const getTaskDateInNY = (dateString?: string | null): string | null => {
  if (!dateString) return null;
  try {
    return formatInTimeZone(new Date(dateString), TASK_TIMEZONE, 'yyyy-MM-dd');
  } catch {
    return null;
  }
};

/**
 * Get today's date as ISO string (YYYY-MM-DD) in NY timezone.
 */
export const getTodayInNY = (): string => {
  return formatInTimeZone(new Date(), TASK_TIMEZONE, 'yyyy-MM-dd');
};

/**
 * Get the current datetime in NY timezone as a Date-like object for comparisons.
 */
const getNowInNY = (): Date => {
  return toZonedTime(new Date(), TASK_TIMEZONE);
};

/**
 * Create consistent date at noon UTC (for task due dates)
 */
export const createConsistentTaskDate = (date: Date): string => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0),
  ).toISOString();
};

// ====== TASK STATUS CHECKS (NY-timezone-aware) ======

/**
 * Check if a task is past due (compared in NY timezone).
 */
export const isTaskPastDue = (
  task: Task,
  options?: { ignoreStatus?: boolean },
): boolean => {
  const dueDateNY = getTaskDateInNY(task.due_date);
  if (!dueDateNY) return false;

  // Exclude completed/not applicable tasks (unless ignoreStatus is true)
  if (
    !options?.ignoreStatus &&
    (task.status.canonical_status === CanonicalTaskStatus.COMPLETED ||
      task.status.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE)
  ) {
    return false;
  }

  const todayNY = getTodayInNY();
  return dueDateNY < todayNY;
};

/**
 * Check if a task is due today (compared in NY timezone).
 */
export const isTaskDueToday = (task: Task): boolean => {
  const dueDateNY = getTaskDateInNY(task.due_date);
  if (!dueDateNY) return false;

  const todayNY = getTodayInNY();
  return dueDateNY === todayNY;
};

/**
 * Check if a task is due tomorrow (compared in NY timezone).
 */
export const isTaskDueTomorrow = (task: Task): boolean => {
  const dueDateNY = getTaskDateInNY(task.due_date);
  if (!dueDateNY) return false;

  const nowInNY = getNowInNY();
  const tomorrowNY = new Date(nowInNY);
  tomorrowNY.setDate(tomorrowNY.getDate() + 1);
  const tomorrowDateNY = formatInTimeZone(
    tomorrowNY,
    TASK_TIMEZONE,
    'yyyy-MM-dd',
  );

  return dueDateNY === tomorrowDateNY;
};

/**
 * Check if a task is due within a specific number of days (compared in NY timezone).
 */
export const isTaskDueWithinDays = (task: Task, days: number): boolean => {
  const dueDateNY = getTaskDateInNY(task.due_date);
  if (!dueDateNY) return false;

  const nowInNY = getNowInNY();
  const targetDate = new Date(nowInNY);
  targetDate.setDate(targetDate.getDate() + days);
  const targetDateNY = formatInTimeZone(
    targetDate,
    TASK_TIMEZONE,
    'yyyy-MM-dd',
  );

  return dueDateNY <= targetDateNY;
};

/**
 * Check if a task is marked as urgent (negative order)
 */
export const isTaskUrgent = (task: Task): boolean => {
  return task.order !== undefined && task.order < 0 && task.order > -100;
};

export const isTaskImmediate = (task: Task): boolean => {
  return task.order !== undefined && task.order <= -100;
};

export const getTaskPriority = (
  task: Task,
): 'immediate' | 'urgent' | 'normal' => {
  if (isTaskImmediate(task)) return 'immediate';
  if (isTaskUrgent(task)) return 'urgent';
  return 'normal';
};

// ====== DATE FORMATTING (NY-timezone-aware) ======

/**
 * Calculate days difference between a task due date and today (both in NY timezone).
 * Returns positive for future dates, negative for past dates.
 */
export const getDaysDiffInNY = (dueDate?: string | null): number | null => {
  const dueDateNY = getTaskDateInNY(dueDate);
  if (!dueDateNY) return null;

  const todayNY = getTodayInNY();

  // Parse both as local dates for comparison (they're already NY-normalized strings)
  const dueMs = new Date(`${dueDateNY}T00:00:00`).getTime();
  const todayMs = new Date(`${todayNY}T00:00:00`).getTime();

  return Math.round((dueMs - todayMs) / (1000 * 60 * 60 * 24));
};

/**
 * Format a due date for display in task lists (NY-timezone-aware).
 */
export const formatTaskDueDate = (dueDate?: string | null): string => {
  if (!dueDate) return '';

  const daysDiff = getDaysDiffInNY(dueDate);
  if (daysDiff === null) return '';

  if (daysDiff < 0) {
    const overdueDays = Math.abs(daysDiff);
    return `${overdueDays} day${overdueDays !== 1 ? 's' : ''} overdue`;
  }

  if (daysDiff === 0) {
    return 'Due today';
  }

  if (daysDiff === 1) {
    return 'Due tomorrow';
  }

  if (daysDiff <= 7) {
    return `Due in ${daysDiff} days`;
  }

  // For dates further out, show the actual date in NY timezone
  return `Due ${formatInTimeZone(new Date(dueDate), TASK_TIMEZONE, 'MMM d')}`;
};

/**
 * Format a due date for compact display (NY-timezone-aware).
 */
export const formatTaskDueDateCompact = (dueDate?: string | null): string => {
  if (!dueDate) return '';

  const daysDiff = getDaysDiffInNY(dueDate);
  if (daysDiff === null) return '';

  if (daysDiff < 0) {
    const overdueDays = Math.abs(daysDiff);
    return `${overdueDays}d`;
  }

  if (daysDiff === 0) {
    return 'Today';
  }

  if (daysDiff === 1) {
    return 'Tomorrow';
  }

  if (daysDiff <= 7) {
    return `${daysDiff}d`;
  }

  // For dates further out, show the actual date in NY timezone
  return formatInTimeZone(new Date(dueDate), TASK_TIMEZONE, 'MMM d');
};

/**
 * Format a relative time (e.g., "2 hours ago", "in 3 days").
 * Note: This uses UTC-based relative time, not NY-specific.
 */
export const formatRelativeTime = (dateString?: string | null): string => {
  const date = parseTaskDate(dateString);
  if (!date) return '';

  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return formatInTimeZone(date, TASK_TIMEZONE, 'MMM d, yyyy');
  }
};

/**
 * Format a task date for display in forms or detailed views (NY-timezone-aware).
 * This is the canonical function for displaying task date fields.
 */
export const formatTaskDate = (
  dateString?: string | null,
  formatString = 'MMM d, yyyy',
): string => {
  if (!dateString) return '';

  try {
    return formatInTimeZone(new Date(dateString), TASK_TIMEZONE, formatString);
  } catch {
    return dateString || '';
  }
};

/**
 * Format task date and time together (NY-timezone-aware).
 */
export const formatTaskDateTime = (dateString?: string | null): string => {
  if (!dateString) return '';

  try {
    return formatInTimeZone(
      new Date(dateString),
      TASK_TIMEZONE,
      'MMM d, yyyy h:mm a',
    );
  } catch {
    return dateString || '';
  }
};

// ====== TASK PRIORITY & GROUPING ======

export type TaskTimeBasedPriority =
  | 'overdue'
  | 'due_24hrs'
  | 'due_5days'
  | 'due_2weeks'
  | 'beyond'
  | 'completed';

/**
 * Get time-based priority for a task
 */
export const getTaskTimeBasedPriority = (
  task: Task,
  options?: { ignoreStatus?: boolean },
): TaskTimeBasedPriority => {
  if (
    !options?.ignoreStatus &&
    (task.status.canonical_status === CanonicalTaskStatus.COMPLETED ||
      task.status.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE)
  ) {
    return 'completed';
  }

  // If no due date, categorize based on status and urgency
  if (!task.due_date) {
    // Blocked tasks are treated as urgent regardless of due date
    if (task.status.canonical_status === CanonicalTaskStatus.BLOCKED) {
      return 'overdue';
    }
    // Tasks marked as urgent (negative order) go to 24hrs category
    if (isTaskUrgent(task)) {
      return 'due_24hrs';
    }
    // Everything else without due date goes to beyond
    return 'beyond';
  }

  // Check date-based priorities
  if (isTaskPastDue(task, options)) {
    return 'overdue';
  }

  if (isTaskDueToday(task) || isTaskDueTomorrow(task)) {
    return 'due_24hrs';
  }

  if (isTaskDueWithinDays(task, 5)) {
    return 'due_5days';
  }

  if (isTaskDueWithinDays(task, 14)) {
    return 'due_2weeks';
  }

  return 'beyond';
};

/**
 * Get display name for time-based priority group
 */
export const getTimeBasedGroupName = (
  priority: TaskTimeBasedPriority,
): string => {
  switch (priority) {
    case 'overdue':
      return 'Overdue';
    case 'due_24hrs':
      return 'Next Action Within 24 Hours';
    case 'due_5days':
      return 'Next Action Within 5 Days';
    case 'due_2weeks':
      return 'Next Action Within 2 Weeks';
    case 'beyond':
      return 'Next Action Beyond 2 Weeks';
    case 'completed':
      return 'Completed';
    default:
      return 'Other';
  }
};

/**
 * Get sort order for time-based priority groups
 */
export const getTimeBasedGroupOrder = (
  priority: TaskTimeBasedPriority,
): number => {
  switch (priority) {
    case 'overdue':
      return 0;
    case 'due_24hrs':
      return 1;
    case 'due_5days':
      return 2;
    case 'due_2weeks':
      return 3;
    case 'beyond':
      return 4;
    case 'completed':
      return 5;
    default:
      return 6;
  }
};

// ====== TASK URGENCY LEVELS ======

export type TaskUrgencyLevel = 'critical' | 'high' | 'medium' | 'low';

/**
 * Get urgency level for a task (for styling and prioritization)
 */
export const getTaskUrgencyLevel = (task: Task): TaskUrgencyLevel => {
  if (isTaskPastDue(task)) return 'critical';

  if (task.status.canonical_status === CanonicalTaskStatus.BLOCKED) {
    return 'high';
  }

  if (isTaskUrgent(task)) return 'high';

  if (isTaskDueToday(task)) return 'critical';

  if (isTaskDueTomorrow(task) || isTaskDueWithinDays(task, 3)) {
    return 'high';
  }

  if (isTaskDueWithinDays(task, 7)) {
    return 'medium';
  }

  return 'low';
};

// ====== TASK SORTING UTILITIES ======

/**
 * Sort tasks within a time-based group
 */
export const sortTasksWithinTimeGroup = (
  tasks: Task[],
  groupName: string,
): Task[] => {
  return [...tasks].sort((a, b) => {
    // For overdue tasks, sort by how overdue they are (most overdue first)
    if (groupName === 'Overdue' && a.due_date && b.due_date) {
      const dueDateA = parseTaskDate(a.due_date);
      const dueDateB = parseTaskDate(b.due_date);
      if (dueDateA && dueDateB) {
        return dueDateA.getTime() - dueDateB.getTime();
      }
    }

    // For other groups, sort by due date (earliest first)
    if (a.due_date && b.due_date) {
      const dueDateA = parseTaskDate(a.due_date);
      const dueDateB = parseTaskDate(b.due_date);
      if (dueDateA && dueDateB) {
        const diff = dueDateA.getTime() - dueDateB.getTime();
        if (diff !== 0) return diff;
      }
    }
    if (a.due_date && !b.due_date) return -1;
    if (!a.due_date && b.due_date) return 1;

    // Then by status priority (blocked first, then in progress, etc.)
    const statusOrder = {
      [CanonicalTaskStatus.BLOCKED]: 0,
      [CanonicalTaskStatus.IN_PROGRESS]: 1,
      [CanonicalTaskStatus.PENDING]: 2,
      [CanonicalTaskStatus.COMPLETED]: 3,
      [CanonicalTaskStatus.CLOSED]: 4,
      [CanonicalTaskStatus.NOT_APPLICABLE]: 5,
    };

    const aStatusOrder = statusOrder[a.status.canonical_status] ?? 6;
    const bStatusOrder = statusOrder[b.status.canonical_status] ?? 6;

    if (aStatusOrder !== bStatusOrder) {
      return aStatusOrder - bStatusOrder;
    }

    // Finally by task order if available (urgent tasks first)
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }

    return 0;
  });
};

/**
 * Sort tasks by overall priority (for urgent lists)
 */
export const sortTasksByPriority = (tasks: Task[]): Task[] => {
  return [...tasks].sort((a, b) => {
    // Sort by urgency level first
    const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const aUrgency = getTaskUrgencyLevel(a);
    const bUrgency = getTaskUrgencyLevel(b);

    if (urgencyOrder[aUrgency] !== urgencyOrder[bUrgency]) {
      return urgencyOrder[aUrgency] - urgencyOrder[bUrgency];
    }

    // Then by due date
    if (a.due_date && b.due_date) {
      const dueDateA = parseTaskDate(a.due_date);
      const dueDateB = parseTaskDate(b.due_date);
      if (dueDateA && dueDateB) {
        return dueDateA.getTime() - dueDateB.getTime();
      }
    }

    return 0;
  });
};

// ====== TASK FILTERING UTILITIES ======

/**
 * Filter tasks that need attention (overdue, due today, urgent)
 */
export const filterTasksNeedingAttention = (tasks: Task[]): Task[] => {
  return tasks.filter((task) => {
    if (
      task.status.canonical_status === CanonicalTaskStatus.COMPLETED ||
      task.status.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE
    ) {
      return false;
    }

    // Include immediate tasks
    if (isTaskImmediate(task)) {
      return true;
    }

    const priority = getTaskTimeBasedPriority(task);
    return priority === 'overdue' || priority === 'due_24hrs';
  });
};

/**
 * Filter urgent tasks for urgent task lists
 */
export const filterUrgentTasks = (tasks: Task[]): Task[] => {
  return tasks.filter((task) => {
    // Exclude completed/not applicable
    if (
      task.status.canonical_status === CanonicalTaskStatus.COMPLETED ||
      task.status.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE
    ) {
      return false;
    }

    // Include urgent tasks, past due, due today, or due soon
    return (
      isTaskUrgent(task) ||
      isTaskPastDue(task) ||
      isTaskDueToday(task) ||
      isTaskDueWithinDays(task, 3)
    );
  });
};
